import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import BackdropLoading from 'components/common/BackdropLoading';
import VoidOrders from 'components/order-void-screen/orders-void-screen';
import { TFunction } from 'i18next';
import React from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { WorkspaceRoutes } from 'types/common';
import { OrderProperty } from 'types/order';
import {
  DRAWER_ORDERS_WIDTH,
  ERROR,
  IDLE,
  LOADING,
  NARRATIVE,
  NOTES,
  NOTIFICATIONS,
  ROUTE_CHARGES,
  ROUTE_CLOSING,
  ROUTE_OVERVIEW,
  ROUTE_DATES_AND_CONFIG,
  ROUTE_DISBURSEMENTS_AND_RECEIPTS,
  ROUTE_DOCUMENTS,
  ROUTE_EARNEST_COMMISSIONS,
  ROUTE_PARTIES,
  ROUTE_PAYMENTS,
  ROUTE_PAYOFFS,
  ROUTE_POLICY_INFO_AND_RATES,
  ROUTE_PROPERTIES,
  ROUTE_TAXES_AND_PRORATIONS,
} from 'utils/constants';
import { CashToClose } from 'views/orders/cash-to-close';
import DatesAndConfig from 'views/orders/dates-and-config/dates-and-config';
import AccountingLedger from 'views/orders/accountingLedger/accountingLedger';
import { Documents } from 'views/orders/documents/new_documents/documents';
import EarnestAndCommissions from 'views/orders/earnest-and-commissions/earnest-and-commissions';
import { Loans } from 'views/orders/loans';
import MortgagePayoffs from 'views/orders/mortgage-payoffs/mortgage-payoffs';
import { Narrative } from 'views/orders/narrative';
import { WireTransfers } from 'views/orders/payments/wire-tranfers';
import { PolicyRefactor } from 'views/orders/policy-info-and-rates-refactor/policy-info-refactor';
import ProjectedPayments from 'views/orders/projected-payments/projected-payments';
import { Properties } from 'views/orders/properties/order-flow-properties';
import RevenueContainer from 'views/orders/revenue/RevenueContainer';
import ScheduleRon from 'views/orders/ron-invite-screen/ron-invite-screen';
import { TaxesAndProrations } from 'views/orders/taxes-and-prorations';
import ClosingDisclosureViewContainer from 'views/orders/closing-disclosure-view';

import Accounting from '../../views/orders/accounting/accounting';
import { Settlement } from 'views/orders/settlement';
import InformationBar from './components/information-bar/information-bar';
import TabOrderOpen from './components/order-overview-tabs';
import Sidemenu from './components/sidemenu';
import { useWorkspaceContext } from 'contexts/workspace-context';
import Notifications from 'views/orders/notifications';
import Parties from 'views/orders/parties/parties';
import Notes from 'views/orders/notes';
import { Charges } from 'views/orders/charges';
import { CurrentUser } from 'contexts/userContext';
import ProtectedRouteOrder from './components/protected-route-order';
import LedgerTestScreen from 'views/orders/ledger-test/LedgerTestScreen';

type OrdersLayoutSceneProps = {
  t: TFunction;
  sidemenuOpen: boolean;
  setSidemenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  statusFetchOrderData: string;
  handleViewChange: (view: WorkspaceRoutes) => void;
  properties: OrderProperty[];
  orderHasAccountingData: boolean;
  isOrderLoading: boolean;
  orderType: string;
  idTitleCompany: string;
  transactionType: string;
  currentUser: CurrentUser | null
};

const useStyles = makeStyles((theme: any) => ({
  containerOpen: {
    marginLeft: DRAWER_ORDERS_WIDTH,
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },
  containerClose: {
    marginLeft: parseFloat(theme.spacing(7)) + 1,
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },
}));

const OrdersLayoutScene = ({
  t,
  sidemenuOpen,
  setSidemenuOpen,
  statusFetchOrderData,
  handleViewChange,
  properties,
  orderHasAccountingData,
  isOrderLoading,
  orderType,
  idTitleCompany,
  transactionType,
  currentUser
}: OrdersLayoutSceneProps) => {
  const { path, url } = useRouteMatch();
  const classes = useStyles();
  const { openedOrderIdList } = useWorkspaceContext();
  if (statusFetchOrderData === LOADING) return <BackdropLoading open loadingText="loading" />;

  if (openedOrderIdList.length === 0 || statusFetchOrderData === (ERROR || IDLE)) {
    return <VoidOrders />;
  }

  return (
    <>
      <Sidemenu
        t={t}
        sidemenuOpen={sidemenuOpen}
        setSidemenuOpen={setSidemenuOpen}
        handleViewChange={handleViewChange}
        orderType={orderType}
        transactionType={transactionType}
        currentUser={currentUser}
      />

      <div
        id="orderViewContent"
        className={clsx({
          [classes.containerOpen]: sidemenuOpen,
          [classes.containerClose]: !sidemenuOpen,
        })}
      >
        <TabOrderOpen />

        <InformationBar
          t={t}
          properties={properties}
          isOrderLoading={isOrderLoading}
        />

        {/* display: contents keeps this marker out of the box tree, so view layout is unchanged. */}
        <div data-screen-root style={{ display: 'contents' }}>
          <Switch>
            <ProtectedRouteOrder exact path={`${path}/${ROUTE_OVERVIEW}`}>
              <ClosingDisclosureViewContainer />
            </ProtectedRouteOrder>

            <Route exact path={`${path}/closing-disclosure`}>
              <Redirect to={`${url}/overview`} />
            </Route>

            <ProtectedRouteOrder path={`${path}/${ROUTE_DATES_AND_CONFIG}`}>
              <DatesAndConfig orderHasAccountingData={orderHasAccountingData} />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder path={`${path}/${ROUTE_PROPERTIES}`}>
              <Properties transactionType={transactionType} />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder path={`${path}/${ROUTE_PARTIES}`}>
              <Parties />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_EARNEST_COMMISSIONS}`}>
              <EarnestAndCommissions />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder path={`${path}/${ROUTE_PAYOFFS}`}>
              <MortgagePayoffs />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_DOCUMENTS}`}>
              <Documents />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/loans/:loanId?`}>
              <Loans properties={properties} orderType={orderType} />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_TAXES_AND_PRORATIONS}`}>
              <TaxesAndProrations properties={properties} />
            </ProtectedRouteOrder>

            <Route exact path={`${path}/${NARRATIVE}`}>
              <Narrative />
            </Route>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_POLICY_INFO_AND_RATES}`}>
              <PolicyRefactor orderType={orderType} />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_CHARGES}`}>
              <Charges orderType={orderType} transactionType={transactionType} />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/projected-payments`}>
              <ProjectedPayments />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/cash-to-close`}>
              <CashToClose />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/revenue`}>
              <RevenueContainer />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_PAYMENTS}/wire-transfers`}>
              <WireTransfers idTitleCompany={idTitleCompany} />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_DISBURSEMENTS_AND_RECEIPTS}`}>
              <AccountingLedger />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${NOTIFICATIONS}`}>
              <Notifications />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${NOTES}`}>
              <Notes />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_PAYMENTS}/accounting`}>
              <Accounting />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_PAYMENTS}/settlement`}>
              <Settlement />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/${ROUTE_CLOSING}/schedule-closing`}>
              <ScheduleRon />
            </ProtectedRouteOrder>

            <ProtectedRouteOrder exact path={`${path}/ledger-test`}>
              <LedgerTestScreen />
            </ProtectedRouteOrder>
          </Switch>
        </div>
      </div>
    </>
  );
};

export default OrdersLayoutScene;
